import { autoinject, singleton, BindingEngine, LogManager } from 'aurelia-framework';
import { ThemeColorService } from './theme-color-service';
import { ItemStatusService } from './item-status-service';
import { ItemTypeDataService } from './item-type-data-service';
import { ClosedFolderService } from './closed-folders-service';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AppUserInfoService } from './app-user-info-service';
import { TagService } from './tag-service';
import { ItemStatus, AppUserInfo, Tag, ItemType, LookupListItem, Item } from '../entities/entities';
import { AppEvents } from 'core/app-events';
import { ItemTypeBase } from '../entities/item-type-base';
import { EntityManager, Repository } from "aurelia-orm";

@autoinject()
export class MetadataService {

	async addTagAsync(tagName: string): Promise<Tag> {
		var newTag = this.tagService.getNew();
		newTag.name = tagName;
		newTag = await newTag.save();
		this.tags._add(newTag);
		return newTag;
    }
	eventSubscriptions: Subscription[] = [];
	logger = LogManager.getLogger('MetadataService');
	itemTypes: ItemType[] = [];
	closedFolders: Item[] = [];
	itemStatuses: ItemStatus[] = []; 
	priorityTypes: ItemTypeBase[] = [];
	importanceTypes: ItemTypeBase[] = [];
	dueStatuses: ItemTypeBase[] = [];
	ownershipStatuses: ItemTypeBase[] = [];
	notificationOptions: ItemTypeBase[] = []
	emailNotificationOptions: ItemTypeBase[] = []
	entityStatuses: ItemTypeBase[] = [];
	itemCloseStatuses: ItemStatus[] = [];
	itemOpenStatuses: ItemStatus[] = [];
	//closeStatuses: ItemTypeBase[] = [];
	timeScaleTypes: LookupListItem[] = [];
	searchTypes: LookupListItem[] = [];
	appUsers: AppUserInfo[] = [];
	statusColors: any = [];
	tags: Tag[] = [];
	processSteps: ItemTypeBase[] = [];
	isInitialized: boolean = false;


	repository: Repository;
	

	constructor(
		private entityManager: EntityManager,
		private themeColorService: ThemeColorService,
		private closedFolderService: ClosedFolderService,
		private itemStatusService: ItemStatusService,
		private itemTypeDataService: ItemTypeDataService,
		private tagService: TagService,
		private appUserInfoService: AppUserInfoService,
		private eventAggregator: EventAggregator	
	) {
		this.repository = this.entityManager.getRepository(LookupListItem);
		this.subscribeToEvents();//This must be done here, as there is no attached or activate for injected classes (ie, not a component or screen).
	}


	private unsubscribeFromEvents() {
		for (let entry of this.eventSubscriptions) {
			entry.dispose();
		}
		this.eventSubscriptions = [];
	}

	private subscribeToEvents() {

		this.eventSubscriptions._add(this.eventAggregator.subscribe(AppEvents.AUTHENTICATION_CHANGE, authenticated => {
			this.onAuthenticated(authenticated);
		}));
		this.eventSubscriptions._add(this.eventAggregator.subscribe(AppEvents.DIRECTORY_CHANGED, payload => {
			this.onDirectoryChanged(payload);
		}));
	}

	onDirectoryChanged(payload) {
		this.clearMetadata();
		this.isInitialized = false;
		this.getMetadata();
	}

	onAuthenticated(authenticated: boolean): void {
		if (!authenticated) {
			this.clearMetadata();
		}
		else {
			this.getMetadata();
		}
	}

	clearMetadata(): any {
		this.isInitialized = false;

		//Clear like this, so that the UI is properly updated.  Otherwise, bound lists do not get cleared, and so duplicates occur in the UI.
		while (this.dueStatuses.length > 0) {
			this.dueStatuses.pop();
		}
		while (this.importanceTypes.length > 0) {
			this.importanceTypes.pop();
		}
		while (this.itemStatuses.length > 0) {
			this.itemStatuses.pop();
		}
		while (this.itemOpenStatuses.length > 0) {
			this.itemOpenStatuses.pop();
		}
		while (this.itemCloseStatuses.length > 0) {
			this.itemCloseStatuses.pop();
		}
		while (this.entityStatuses.length > 0) {
			this.entityStatuses.pop();
		}

		while (this.statusColors.length > 0) {
			this.statusColors.pop();
		}
		while (this.itemTypes.length > 0) {
			this.itemTypes.pop();
		}
		while (this.priorityTypes.length > 0) {
			this.priorityTypes.pop();
		}
		while (this.timeScaleTypes.length > 0) {
			this.timeScaleTypes.pop();
		}
		while (this.appUsers.length > 0) {
			this.appUsers.pop();
		}
		while (this.tags.length > 0) {
			this.tags.pop();
		}
		while (this.ownershipStatuses.length > 0) {
			this.ownershipStatuses.pop();
		}
		while (this.notificationOptions.length > 0) {
			this.notificationOptions.pop();
		}
		while (this.emailNotificationOptions.length > 0) {
			this.emailNotificationOptions.pop();
		}
		while (this.closedFolders.length > 0) {
			this.closedFolders.pop();
		}
		//while (this.closeStatuses.length > 0) {
		//	this.importanceTypes.pop();
		//}
	}

	async getMetadata() {
		//if (this.isInitialized) return;
		//this.isInitialized = true; //Put first to prevent it from getting twice at the same time.
		this.logger.info("Getting Metadata");
		let promises = [];
		promises._add(this.getItemTypes());
		promises._add(this.getEntityStatusTypes())
		promises._add(this.getItemStatuses());
		//promises._add(this.getCloseStatuses());
		promises._add(this.getStatusColors());
		promises._add(this.getClosedFolders());
		promises._add(this.getImportanceTypes());
		promises._add(this.getPriorityTypes());
		promises._add(this.getProcessSteps());
		promises._add(this.getTimeScaleTypes());
		promises._add(this.getTags());
		promises._add(this.getAppUsers());
		promises._add(this.getDueStatuses());
		promises._add(this.getSearchTypes());
		promises._add(this.getOwnershipStatuses());
		promises._add(this.getNotificationOptions());
		promises._add(this.getEmailNotificationOptions());
		try {
			await Promise.all(promises);
			this.isInitialized = true;
			this.eventAggregator.publish(AppEvents.MetadataAcquired);
			this.logger.info("metadata complete");
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	async getAppUsers() {
		try {
			
			const items = await this.appUserInfoService.getActiveTeamMembers();
			while (this.appUsers.length > 0) {
				this.appUsers.pop();
			}
			items.forEach((item) => this.appUsers._add(item));
			//this.appUsers = items;
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	async getTags() {
		try {
			const items = await this.tagService.getTags();
			while (this.tags.length > 0) {
				this.tags.pop();
			}
			this.tags = items;
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	async getStatusColors(): Promise<void> {
		try {
			const items = await this.themeColorService.list(1, 1);//ToDo:Expand Themes and Types    
			while (this.statusColors.length > 0) {
				this.statusColors.pop();
			}
			items.forEach((item) => this.statusColors._add(item));
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	async getClosedFolders(): Promise<void> {
		try {
			const items = await this.closedFolderService.list();//ToDo:Expand Themes and Types    
			while (this.closedFolders.length > 0) {
				this.closedFolders.pop();
			}
			items.forEach((item) => this.closedFolders._add(item));
		}
		catch (error) {
			this.logger.error(error);
		}
	}

	async getItemTypes() {
		const items = await this.itemTypeDataService.list();
		while (this.itemTypes.length > 0) {
			this.itemTypes.pop();
		}
		items.forEach((item) => this.itemTypes._add(item));
	}

	async getItemStatuses(): Promise<void> {

		

		const newItems = await this.itemStatusService.list();
		while (this.itemStatuses.length > 0) {
			this.itemStatuses.pop();
		}
		newItems.forEach((item) => this.itemStatuses._add(item));
		
		this.createClosedItemStatusList(newItems);

		this.createOpenItemStatusList(newItems);

		//this.createItemStatusSearchList(newItems);

	}

	itemStatusSearchOptions: ItemStatus[] = [];

  //  createItemStatusSearchList(newItems: ItemStatus[]): any {
		//const itemStatusSearchOptions = newItems.filter(a => a.itemStatusTypeId === 1 || a.itemStatusTypeId === 3);
		//while (this.itemStatusSearchOptions.length > 0) {
		//	this.itemStatusSearchOptions.pop();
		//}
		//itemStatusSearchOptions.forEach((item) => this.itemStatusSearchOptions._add(item));
  //  }

    private createOpenItemStatusList(newItems: ItemStatus[]) {
        const itemOpenStatuses = newItems.filter(a => a.itemStatusTypeId === 1);
        while (this.itemOpenStatuses.length > 0) {
            this.itemOpenStatuses.pop();
        }
        itemOpenStatuses.forEach((item) => this.itemOpenStatuses._add(item));
    }

    private createClosedItemStatusList(newItems: ItemStatus[]) {
        const itemCloseStatuses = newItems.filter(a => a.itemStatusTypeId === 2);
        while (this.itemCloseStatuses.length > 0) {
            this.itemCloseStatuses.pop();
        }
        itemCloseStatuses.forEach((item) => this.itemCloseStatuses._add(item));
    }

	async getTimeScaleTypes(): Promise<void> {
		
		const items = await this.repository.find('TimeScaleTypes');// timeScaleTypeService.list();
		while (this.timeScaleTypes.length > 0) {
			this.timeScaleTypes.pop();
		}
		items.forEach((item) => this.timeScaleTypes._add(item));
		
	}

	async getEntityStatusTypes(): Promise<void> {
		//Clear the list
		
		const items = await this.repository.find('EntityStatuses');// entityStatusTypeService.list();
		while (this.entityStatuses.length > 0) {
			this.entityStatuses.pop();
		}
		items.forEach((item) => this.entityStatuses._add(item));
	}

	async getImportanceTypes(): Promise<void> {

		const items = await this.repository.find('ImportanceTypes');// importanceTypeService.list();
		while (this.importanceTypes.length > 0) {
			this.importanceTypes.pop();
		}
		items.forEach((item) => this.importanceTypes._add(item));
	}

	async getSearchTypes(): Promise<void> {

		const items = await this.repository.find('searchtypes', true);
		while (this.searchTypes.length > 0) {
			this.searchTypes.pop();
		}
		items.forEach((item) => this.searchTypes._add(item));
	}

	//Handled differently, as these are not used by the grid.
	//The are used by the UI, and not clearing out like this results in duplicates.
	async getDueStatuses(): Promise<void> {

		const items = await this.repository.find('DueStatuses', true);
		while (this.dueStatuses.length > 0) {
			this.dueStatuses.pop();
		}
		items.forEach((item) => this.dueStatuses._add(item));
	}

	//Handled differently, as these are not used by the grid.
	async getOwnershipStatuses(): Promise<void> {
		const items = await this.repository.find('OwnershipStatuses', true);
		while (this.ownershipStatuses.length > 0) {
			this.ownershipStatuses.pop();
		}
		items.forEach((item) => this.ownershipStatuses._add(item));		
	}

	//Handled differently, as these are not used by the grid.
	async getNotificationOptions(): Promise<void> {
		const items = await this.repository.find('NotificationOptions', true);
		while (this.notificationOptions.length > 0) {
			this.notificationOptions.pop();
		}
		items.forEach((item) => this.notificationOptions._add(item));
	}

	//Handled differently, as these are not used by the grid.
	async getEmailNotificationOptions(): Promise<void> {
		const items = await this.repository.find('EmailNotificationOptions', true);
		while (this.emailNotificationOptions.length > 0) {
			this.emailNotificationOptions.pop();
		}
		items.forEach((item) => this.emailNotificationOptions._add(item));
	}

	async getProcessSteps(): Promise<void> {

		const processSteps= await this.repository.find('ProcessSteps');// priorityTypeService.list();
		//Clear the list
		while (this.processSteps.length > 0) {
			this.processSteps.pop();
		}

		processSteps.forEach((item) => this.processSteps._add(item));
	}

	async getPriorityTypes(): Promise<void> {
		
		const priorityTypes = await this.repository.find('PriorityTypes');// priorityTypeService.list();
		//Clear the list
		while (this.priorityTypes.length > 0) {
			this.priorityTypes.pop();
		}

		priorityTypes.forEach((item) => this.priorityTypes._add(item));
	}

}